import { Grid, TextField, Box, Autocomplete, Chip } from "@mui/material";
import * as React from "react";
import DeviceClassSelect from "../selects/device_class_select";
import USBPortSelect from "../selects/usb_port_select";
import HelpedSelect from "../../general/helped_select";
import { Protocol } from "../../../model/device";
import BatteryTypeSelect from "../selects/battery_type_select";
import MeterTypeSelect from "../selects/meter_type_select";
import { processValue } from "../../../scripts/utils";
import SolarInverterTypeSelect from "../selects/solar_inverter_type_select";
import Parity from "../selects/parity_select";

const TypeSelect = React.memo(
  function TypeSelect({ deviceClass, deviceType, onChange }) {
    const handleChange = (e) => {
      onChange("device_type", e.target.value, true);
    };

    if (deviceClass === "battery") {
      return <BatteryTypeSelect value={deviceType} onChange={handleChange} />;
    } else if (deviceClass === "meter") {
      return <MeterTypeSelect value={deviceType} onChange={handleChange} />;
    } else if (deviceClass === "solar_inverter") {
      return <SolarInverterTypeSelect value={deviceType} onChange={handleChange} />;
    }
  },
  (oldProps, newProps) => {
    return oldProps.deviceClass === newProps.deviceClass && oldProps.deviceType === newProps.deviceType;
  },
);

function OptionalInput({ label, fieldKey, settings, handleChange, isString, componentProps }) {
  return (
    <TextField
      sx={{ minWidth: "15%", mr: 1 }}
      key={fieldKey}
      label={label}
      value={settings[fieldKey]}
      onChange={(e) => handleChange(fieldKey, e.target.value, isString)}
      {...componentProps}
    />
  );
}

function OptionalParitySelect({ label, fieldKey, settings, handleChange, isString, componentProps }) {
  return (
    <Parity
      sx={{ minWidth: "15%", mr: 2 }}
      key={fieldKey}
      label={label}
      value={settings[fieldKey]}
      onChange={(e) => handleChange(fieldKey, e.target.value, isString)}
      {...componentProps}
    />
  );
}

// function OptionalSerialSelect({label, fieldKey, settings, handleChange, isString, componentProps}) {
//     return <USBPortSelect sx={{minWidth: "15%", mr: 2}} key={fieldKey} label={label} value={settings[fieldKey]} onChange={(e) => handleChange(fieldKey, e.target.value, isString)} {...componentProps}/>
// }

const OPTIONAL_SETTINGS = {
  slave: { name: "Modbus slave", fieldKey: "slave", component: OptionalInput, isString: false },
  port: { name: "Modbus port", fieldKey: "port", component: OptionalInput, isString: false },
  parity: { name: "Parity", fieldKey: "parity", component: OptionalParitySelect, isString: true },
  baudrate: { name: "Baudrate", fieldKey: "baudrate", component: OptionalInput, isString: false },
  stopbits: { name: "Stopbits", fieldKey: "stopbits", component: OptionalInput, isString: false },
  // "serial-address": {name: "Serial address", fieldKey:"address", component: OptionalSerialSelect, isString: true}
};

function OptionalSettingsSelect({ optionalSettings, setOptionalSettings, handleChange }) {
  const handleDelete = (value) => {
    handleChange(value.fieldKey, null, true, true);
    setOptionalSettings((current) => current.filter((obj) => obj.fieldKey != value.fieldKey));
  };

  return (
    <Box margin={2} display="flex" width="100%">
      <Autocomplete
        multiple
        fullWidth
        value={optionalSettings}
        options={Object.values(OPTIONAL_SETTINGS)}
        renderInput={(params) => <TextField {...params} label="Optional settings" />}
        getOptionLabel={(option) => option.name}
        renderTags={(values) =>
          values.map((value) => {
            return <Chip key={value.fieldKey} label={value.name} sx={{ ml: 1 }} onDelete={() => handleDelete(value)} />;
          })
        }
        isOptionEqualToValue={(option, value) => option.fieldKey === value.fieldKey}
        onChange={(event, newValue) => setOptionalSettings(newValue)}
        blurOnSelect
        disableClearable
      />
    </Box>
  );
}

function OptionalSettings({ settings, optionalSettings, handleSettingsChange }) {
  return (
    <Box display="flex" width="100%" sx={{ mx: 2 }}>
      {optionalSettings.map((obj, idx) =>
        obj.component({
          label: obj.name,
          fieldKey: obj.fieldKey,
          isString: obj.isString,
          settings: settings,
          handleChange: handleSettingsChange,
        }),
      )}
    </Box>
  );
}

export const ProbeSettings = ({ settings, setSettings }) => {
  const [optionalSettings, setOptionalSettings] = React.useState([]);

  function handleSettingsChange(field, value, isString = false, nullable = false) {
    if (nullable && value === null) {
      setSettings((prev) => {
        const updated = { ...prev };
        updated[field] = null;
        return updated;
      });
      return;
    } else {
      const processed = processValue(value, isString);
      setSettings((prev) => {
        const updated = { ...prev };
        updated[field] = processed;
        return updated;
      });
    }
  }

  const gridItemSettings = {
    md: 6,
    xs: 12,
    p: 2,
    width: "80%",
    sx: { display: "flex", alignItems: "center", justifyContent: "center" },
  };

  return (
    <>
      <Grid container justifyContent="start">
        <Grid item {...gridItemSettings}>
          <DeviceClassSelect
            fullWidth
            value={settings.device_class}
            onChange={(e) => {
              handleSettingsChange("device_type", "", true);
              handleSettingsChange("device_class", e.target.value, true);
            }}
          />
        </Grid>
        <Grid item {...gridItemSettings}>
          <TypeSelect
            deviceClass={settings.device_class}
            deviceType={settings.device_type}
            onChange={handleSettingsChange}
          />
        </Grid>
        <Grid item {...gridItemSettings}>
          <HelpedSelect
            fullWidth
            options={Protocol}
            label={"Protocol"}
            key="protocol"
            info="Select the communication protocol between elion box and device"
            selected={settings.protocol}
            onChange={(e) => {
              handleSettingsChange("protocol", e.target.value, true);
              handleSettingsChange("address", "", true);
            }}
          />
        </Grid>
        <Grid item {...gridItemSettings}>
          {settings.protocol !== Protocol.RS485 && (
            <TextField
              label="IP address"
              fullWidth
              value={settings.address}
              onChange={(e) => handleSettingsChange("address", e.target.value, true)}
            />
          )}
        </Grid>
      </Grid>
      <OptionalSettingsSelect
        handleChange={handleSettingsChange}
        optionalSettings={optionalSettings}
        setOptionalSettings={setOptionalSettings}
      />
      <OptionalSettings
        settings={settings}
        optionalSettings={optionalSettings}
        handleSettingsChange={handleSettingsChange}
      />
    </>
  );
};
